import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Session storage key for scroll positions
const SCROLL_POSITIONS_KEY = 'app-scroll-positions';

// Get stored scroll positions
function getScrollPositions(): Record<string, number> {
  try {
    const stored = sessionStorage.getItem(SCROLL_POSITIONS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Save scroll position for a path
function saveScrollPosition(path: string, position: number) {
  try {
    const positions = getScrollPositions();
    positions[path] = position;
    sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions));
  } catch {
    // Ignore storage errors
  }
}

// Get scroll position for a path
function getScrollPosition(path: string): number {
  const positions = getScrollPositions();
  return positions[path] || 0;
}

// Clear scroll position for a path
function clearScrollPosition(path: string) {
  try {
    const positions = getScrollPositions();
    delete positions[path];
    sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook to save scroll position before navigating away
 * Call saveCurrentScroll() before navigation to preserve position
 */
export function useScrollSave() {
  const location = useLocation();
  
  const saveCurrentScroll = useCallback(() => {
    const scrollY = window.scrollY;
    saveScrollPosition(location.pathname, scrollY);
  }, [location.pathname]);
  
  return { saveCurrentScroll };
}

/**
 * Hook to restore scroll position when returning to a page
 * Automatically restores position on mount if previously saved
 */
export function useScrollRestore() {
  const location = useLocation();
  const hasRestored = useRef(false);
  
  useEffect(() => {
    // Only restore once per mount
    if (hasRestored.current) return;
    hasRestored.current = true;
    
    const savedPosition = getScrollPosition(location.pathname);
    
    if (savedPosition > 0) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, savedPosition);
        // Clear after restore to avoid stale positions
        clearScrollPosition(location.pathname);
      });
    }
  }, [location.pathname]);
}

/**
 * Combined hook for pages that need both save and restore
 */
export function useScrollMemory() {
  const { saveCurrentScroll } = useScrollSave();
  useScrollRestore();
  
  return { saveCurrentScroll };
}
